import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash-es';

import { IAppliedRewards } from 'state/loyalty/hooks/types';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

import { IRewardsState } from './rewards.types';

export const getAppliedRewardsFromStorage = () => {
  return syncStorage.getObject<IAppliedRewards>(SyncStorageKey.APPLIED_LOYALTY_REWARDS) ?? {};
};

export const updateAppliedRewardsInStorage = (appliedReward: IAppliedRewards) => {
  syncStorage.setObject(SyncStorageKey.APPLIED_LOYALTY_REWARDS, appliedReward);
};

export const removeAppliedRewardsInStorage = () => {
  syncStorage.remove(SyncStorageKey.APPLIED_LOYALTY_REWARDS);
};

export const computeTotalTimesRewardApplied = (
  appliedRewards: IAppliedRewards
): Record<string, number> => {
  return Object.values(appliedRewards).reduce(
    (acc: Record<string, number>, { rewardId, timesApplied }) => {
      if (timesApplied) {
        acc[rewardId] = (acc[rewardId] || 0) + timesApplied;
      }
      return acc;
    },
    {}
  );
};

export const setStateFromAppliedRewards = (
  state: IRewardsState,
  appliedRewards: IAppliedRewards
) => {
  updateAppliedRewardsInStorage(appliedRewards);
  if (!isEqual(state.appliedLoyaltyRewards, appliedRewards)) {
    state.appliedLoyaltyRewards = appliedRewards;
  }
};

export const getAppliedPointsInCart = createSelector(
  (state: IRewardsState) => state.appliedLoyaltyRewards,
  (state: IRewardsState) => state.availableLoyaltyRewardsMap,
  (appliedLoyaltyRewards, availableLoyaltyRewardsMap) => {
    return Object.values(appliedLoyaltyRewards).reduce((acc, { rewardBenefitId, timesApplied }) => {
      const { pointCost } = availableLoyaltyRewardsMap[rewardBenefitId] || {};
      return acc + (pointCost || 0) * timesApplied;
    }, 0);
  }
);

/**
 * Total number of reward applications committed across all cart entries.
 * Use this (not `Object.keys(applied).length`) to enforce `rewardRedemptionLimit`, which caps
 * total applications rather than distinct cart lines (a single line can have timesApplied > 1
 * in the in-restaurant flow).
 */
export const sumAppliedTimesApplied = (appliedRewards: IAppliedRewards): number =>
  Object.values(appliedRewards).reduce((sum, entry) => sum + (entry?.timesApplied ?? 0), 0);

/**
 * When adding a reward on `newCartId` and unstacking is capped at `rewardRedemptionLimit` total
 * applications, returns the cart id of the oldest applied reward to drop (insertion order), if any.
 */
export const getCartIdToEvictForNewRewardSlot = (
  appliedRewards: IAppliedRewards,
  newCartId: string,
  rewardRedemptionLimit: number
): string | undefined => {
  const cap = Math.max(1, rewardRedemptionLimit);
  if (appliedRewards[newCartId]) {
    return undefined;
  }
  const keys = Object.keys(appliedRewards);
  if (sumAppliedTimesApplied(appliedRewards) < cap) {
    return undefined;
  }
  return keys[0];
};

export const getStagedCartPoints = (state: IRewardsState, basePoints: number) => {
  const appliedPointsInCart = getAppliedPointsInCart(state);
  const availablePointsToUse = basePoints - appliedPointsInCart;
  const isValid = 0 <= availablePointsToUse && availablePointsToUse <= basePoints;
  if (isValid) {
    return availablePointsToUse;
  }
  // TODO: on the else case, analyze if we need to reset some props if we find a discrepancy
  return null;
};
